import {
  BR_FEATURE_CONFIG,
  brRuntimeState,
  loadBrSubmissionStatus,
} from '../../../constants/businessRegistration';
import { BusinessRegistrationService } from '../../../_services';
import {
  isAllowedBrFile,
  normalizeBrOcrPayload,
  evaluateBrScanResult,
  resolveBrSaveExpiryDate,
  resolveBrSaveIssueDate,
  resolveBrConfirmDatesFromOcr,
  unwrapOcrPayload,
} from '../utils/brOcr';
import {
  createEmptyBrConfirmForm,
  findPendingBrDuplicatesByRegistrationNo,
} from '../utils/brConfirmSubmit';

/**
 * Add Document：上传 / OCR / 确认提交（供应商与管理端代传共用）。
 * 依赖宿主：fromAdmin、targetEmpCompanyId、targetAccountId、submitType、
 * displayNameEn/Zh、currentStep、withTargetCompany（见页面）。
 */
export default {
  data() {
    return {
      selectedFile: null,
      selectedFileName: '',
      previewUrl: '',
      /** OCR 階段已上傳至 OSS 的 URL（BusinessLicenseImage），提交時直接複用 */
      ossMediaUrl: '',
      scanning: false,
      saving: false,
      ocrRaw: null,
      /** 扫描告警与阻断同源（evaluateBrScanResult.alertKeys === blockerKeys） */
      scanBlockerKeys: [],
      confirmForm: createEmptyBrConfirmForm(),
      /** 每次扫描开始递增，确认表单触碰态重置 */
      scanResetToken: 0,
      showDuplicatePendingConfirm: false,
      pendingSubmitFormData: null,
      showSubmitSuccess: false,
      /** 管理端提交前临时拉取的证件列表（查同号待审） */
      adminDocumentList: [],
    };
  },
  beforeDestroy() {
    this.revokePreviewUrl();
  },
  methods: {
    triggerFileInput() {
      if (this.scanning) {
        return;
      }
      this.$refs.uploadSection?.triggerFileInput();
    },
    onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      this.handleFile(file);
      if (event.target) {
        event.target.value = '';
      }
    },
    onDrop(event) {
      if (this.scanning) {
        return;
      }
      this.isDragOver = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      this.handleFile(file);
    },
    revokePreviewUrl() {
      if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.previewUrl);
      }
      this.previewUrl = '';
    },
    async handleFile(file) {
      if (!file) {
        return;
      }
      if (BR_FEATURE_CONFIG.minFileSizeBytes && file.size < BR_FEATURE_CONFIG.minFileSizeBytes) {
        this.notifyBrUploadError('file_size');
        return;
      }
      if (BR_FEATURE_CONFIG.maxFileSizeBytes && file.size > BR_FEATURE_CONFIG.maxFileSizeBytes) {
        this.notifyBrUploadError('file_size');
        return;
      }
      if (!isAllowedBrFile(file)) {
        this.notifyBrUploadError('file_type');
        return;
      }

      const stayOnConfirm = this.currentStep === 'confirm';
      this.revokePreviewUrl();
      this.selectedFile = file;
      this.selectedFileName = file.name;
      this.ossMediaUrl = '';
      this.previewUrl = URL.createObjectURL(file);
      this.ocrRaw = null;
      this.scanBlockerKeys = [];
      // 从确认页重新上传时保持 Step 2，不跳回上传页
      if (!stayOnConfirm) {
        this.currentStep = 'upload';
      }
      await this.scanFile(file);
    },
    /** 上传前置校验失败：仅上传区红框 + 红字（BrScanAlert），不弹框 */
    notifyBrUploadError(alertKey) {
      this.scanBlockerKeys = alertKey ? [alertKey] : [];
    },
    populateConfirmForm(normalized = {}) {
      const dates = resolveBrConfirmDatesFromOcr(normalized);
      this.confirmForm = {
        registeredCompanyName: normalized.companyName || '',
        registrationNo: normalized.registrationNo || '',
        hasExpirationDate: dates.hasExpirationDate,
        startDate: dates.startDate,
        expirationDate: dates.expirationDate,
      };
    },
    resetUploadState() {
      this.revokePreviewUrl();
      this.selectedFile = null;
      this.selectedFileName = '';
      this.ossMediaUrl = '';
      this.ocrRaw = null;
      this.scanBlockerKeys = [];
      this.confirmForm = createEmptyBrConfirmForm();
      this.currentStep = 'upload';
    },
    /** Step 2「点击重新上传」：不离开确认页，仅弹出选文件 */
    handleReupload() {
      if (this.scanning || this.saving) {
        return;
      }
      this.$refs.confirmReuploadInput?.click();
    },
    applyScanResult(evaluation, normalized = {}) {
      this.scanBlockerKeys = evaluation.blockerKeys || [];
      this.populateConfirmForm(normalized);
      if (evaluation.canProceed) {
        this.currentStep = 'confirm';
        return;
      }
      // 确认页重新上传失败：留在 Step 2 展示错误；首次上传失败仍回 Step 1
      if (this.currentStep !== 'confirm') {
        this.currentStep = 'upload';
      }
    },
    applyScanFailure(errorOrRes) {
      const evaluation = evaluateBrScanResult({}, {
        scanFailed: true,
        httpStatus: errorOrRes?.response?.status || errorOrRes?.httpStatus,
      });
      this.applyScanResult(evaluation, {});
    },
    async scanFile(file) {
      this.scanning = true;
      this.scanBlockerKeys = [];
      this.scanResetToken += 1;
      this.confirmForm = createEmptyBrConfirmForm();
      try {
        const res = await BusinessRegistrationService.ScanBusinessLicense(file);
        if (!res?.isSuccessful) {
          this.applyScanFailure(res);
          return;
        }

        const payload = unwrapOcrPayload(res?.data || {});
        this.ocrRaw = payload;
        const normalized = normalizeBrOcrPayload(payload);

        if (normalized.businessLicenseImage) {
          this.ossMediaUrl = normalized.businessLicenseImage;
          if (!this.previewUrl) {
            this.previewUrl = normalized.businessLicenseImage;
          }
        }

        const evaluation = evaluateBrScanResult(normalized, { alreadyNormalized: true });
        this.applyScanResult(evaluation, normalized);
      } catch (error) {
        this.applyScanFailure(error);
      } finally {
        this.scanning = false;
      }
    },
    buildSavePayload(formData) {
      const normalized = normalizeBrOcrPayload(this.ocrRaw || {});
      return this.withTargetCompany({
        companyName: formData.registeredCompanyName,
        registrationNo: formData.registrationNo,
        issueDate: resolveBrSaveIssueDate(formData),
        expiryDate: resolveBrSaveExpiryDate(formData),
        mediaFileName: this.ossMediaUrl,
        ocrPayloadJson: this.ocrRaw ? JSON.stringify(this.ocrRaw) : '',
        ocrCompanyName: normalized.companyName || '',
        ocrRegistrationNo: normalized.registrationNo || '',
        displayNameEn: (formData.displayNameEn || '').trim() || this.displayNameEn || '',
        displayNameZh: (formData.displayNameZh || '').trim() || this.displayNameZh || '',
      });
    },
    async saveAndEnqueue(formData) {
      const saveRes = await BusinessRegistrationService.SaveDocument(this.buildSavePayload(formData));
      if (!saveRes?.isSuccessful) {
        throw new Error(saveRes?.systemErrorMsg?.[0] || saveRes?.msg || 'Save failed');
      }
      const businessRegistrationId = saveRes?.data?.businessRegistrationId
        || saveRes?.data?.BusinessRegistrationId;
      const enqueuePayload = this.withTargetCompany({
        businessRegistrationId,
      });
      const submitType = String(this.submitType || '').trim();
      if (submitType) {
        enqueuePayload.submitType = submitType;
      }
      const enqueueRes = await BusinessRegistrationService.EnqueueForApproval(enqueuePayload);
      if (!enqueueRes?.isSuccessful) {
        throw new Error(enqueueRes?.systemErrorMsg?.[0] || enqueueRes?.msg || 'Enqueue failed');
      }
    },
    async onSubmitSuccess() {
      if (this.fromAdmin) {
        this.$toast?.success?.(this.$t('lbl_EMPBE_BR_Submit_Success'));
        this.$emit('submitted');
        return;
      }
      await loadBrSubmissionStatus('hard');
      this.currentStep = 'pending';
      this.showSubmitSuccess = true;
    },
    closeSubmitSuccess() {
      this.showSubmitSuccess = false;
    },
    async resolveDocumentListForDuplicateCheck() {
      if (!this.fromAdmin) {
        return brRuntimeState.documentList || [];
      }
      if (this.adminDocumentList.length) {
        return this.adminDocumentList;
      }
      // 优先用管理端列表页已加载的单源数据，避免再打 GetList
      const seeded = Array.isArray(this.seedDocumentList) ? this.seedDocumentList : [];
      if (seeded.length) {
        this.adminDocumentList = seeded.slice();
        return this.adminDocumentList;
      }
      try {
        const res = await BusinessRegistrationService.GetList({
          empCompanyId: this.targetEmpCompanyId,
        });
        if (!res?.isSuccessful) {
          this.$toast?.error?.(this.$t('lbl_EMPBE_BR_List_LoadFailed'));
          this.adminDocumentList = [];
          return this.adminDocumentList;
        }
        const rows = res?.data?.items || res?.data?.Items || [];
        this.adminDocumentList = Array.isArray(rows) ? rows : [];
      } catch (e) {
        this.$toast?.error?.(this.$t('lbl_EMPBE_BR_List_LoadFailed'));
        this.adminDocumentList = [];
      }
      return this.adminDocumentList;
    },
    closeDuplicatePendingConfirm() {
      if (this.saving) {
        return;
      }
      this.showDuplicatePendingConfirm = false;
      this.pendingSubmitFormData = null;
    },
    async confirmDuplicatePendingSubmit() {
      const formData = this.pendingSubmitFormData;
      if (!formData || this.saving) {
        return;
      }
      this.showDuplicatePendingConfirm = false;
      this.pendingSubmitFormData = null;
      await this.executeSubmitForReview(formData);
    },
    async submitForReview(formData) {
      if (!this.selectedFile || this.scanBlockerKeys.length) {
        return;
      }
      if (!this.ossMediaUrl) {
        this.$toast?.error?.(this.$t('lbl_EMPBE_BR_Alert_ScanFailed'));
        return;
      }
      if (this.saving || this.showDuplicatePendingConfirm) {
        return;
      }
      const list = await this.resolveDocumentListForDuplicateCheck();
      const duplicates = findPendingBrDuplicatesByRegistrationNo(
        list,
        formData?.registrationNo,
      );
      if (duplicates.length) {
        this.pendingSubmitFormData = formData;
        this.showDuplicatePendingConfirm = true;
        return;
      }
      await this.executeSubmitForReview(formData);
    },
    async executeSubmitForReview(formData) {
      this.saving = true;
      try {
        await this.saveAndEnqueue(formData);
        await this.onSubmitSuccess();
      } catch (error) {
        this.$toast?.error?.(error?.message || this.$t('lbl_EMPBE_BR_Submit_Failed'));
      } finally {
        this.saving = false;
      }
    },
  },
};
